import { KIND_LABEL } from './referenceKinds';
import type { Reference } from './referenceKinds';

/** Where a query word was found, best first. */
const enum MatchStrength {
    NamePrefix = 0,
    Name = 1,
    KindLabel = 2,
    Detail = 3,
    None = 4,
}

function strengthForNameWord(name: string, word: string): MatchStrength {
    if (name.startsWith(word)) {
        return MatchStrength.NamePrefix;
    }
    // A word boundary counts as a prefix, so "podcast" ranks "Soccer Podcast"
    // as strongly as "Podcast Weekly".
    if (name.split(/\s+/).some((part) => part.startsWith(word))) {
        return MatchStrength.NamePrefix;
    }
    if (name.includes(word)) {
        return MatchStrength.Name;
    }
    return MatchStrength.None;
}

function strengthForWord(reference: Reference, word: string): MatchStrength {
    const name = reference.name.toLowerCase();
    const nameStrength = strengthForNameWord(name, word);
    if (nameStrength !== MatchStrength.None) {
        return nameStrength;
    }
    // Lets "@temp" surface every template, not just ones named "template".
    if (KIND_LABEL[reference.kind].toLowerCase().includes(word)) {
        return MatchStrength.KindLabel;
    }
    if (reference.detail?.toLowerCase().includes(word)) {
        return MatchStrength.Detail;
    }
    return MatchStrength.None;
}

// Trailing whitespace is dropped: the space that keeps the menu open would
// otherwise filter everything out.
export function queryWords(queryString: string | null): string[] {
    return (queryString ?? '').toLowerCase().trim().split(/\s+/).filter(Boolean);
}

/**
 * Lower is better, `null` for no match. Every word must match something, in any
 * order; scoring by the weakest field matched ranks stronger fields above weaker.
 */
function scoreWords(words: string[], strengthFor: (word: string) => MatchStrength): number | null {
    if (words.length === 0) {
        return 0;
    }

    let worst = MatchStrength.NamePrefix;
    for (const word of words) {
        const strength = strengthFor(word);
        if (strength === MatchStrength.None) {
            return null;
        }
        worst = Math.max(worst, strength);
    }
    return worst;
}

/** Matches on name, kind label and detail. */
export function scoreReference(reference: Reference, words: string[]): number | null {
    return scoreWords(words, (word) => strengthForWord(reference, word));
}

/** For menus whose rows are plain named things, with no kind or detail to search. */
export function scoreByName(name: string, words: string[]): number | null {
    const lowerCaseName = name.toLowerCase();
    return scoreWords(words, (word) => strengthForNameWord(lowerCaseName, word));
}
